import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiDownload, FiFileText, FiCheck, FiChevronDown, FiChevronUp, FiExternalLink } from 'react-icons/fi';
import { getPlanById } from '@/config/planConfig';

interface Invoice {
  id: string;
  plan_id: string;
  amount_usd: number;
  currency: string;
  payment_method: string;
  event_source: string;
  status: string;
  created_at: string;
  invoice_number: string | null;
  billing_period_start: string | null;
  billing_period_end: string | null;
  billing_type: string | null;
}

interface InvoiceCardProps {
  invoice: Invoice;
  userEmail?: string;
  userName?: string;
}

const InvoiceCard: React.FC<InvoiceCardProps> = ({ invoice, userEmail, userName }) => {
  const [expanded, setExpanded] = useState(false);
  
  const plan = getPlanById(invoice.plan_id);
  const invoiceDate = new Date(invoice.created_at);
  
  // Use provided invoice_number or generate from ID and date
  const invoiceNumber = invoice.invoice_number || 
    `INV-${invoiceDate.getFullYear()}${String(invoiceDate.getMonth() + 1).padStart(2, '0')}-${invoice.id.slice(0, 8).toUpperCase()}`;
  
  // Use provided billing period or calculate from invoice date
  const periodStart = invoice.billing_period_start ? new Date(invoice.billing_period_start) : invoiceDate;
  const periodEnd = invoice.billing_period_end 
    ? new Date(invoice.billing_period_end)
    : (() => {
        const end = new Date(periodStart);
        if (invoice.billing_type === 'annual' || invoice.billing_type === 'lifetime') {
          end.setFullYear(end.getFullYear() + 1);
        } else {
          end.setMonth(end.getMonth() + 1);
        }
        return end;
      })();

  // Determine if crypto payment
  const isCrypto = invoice.event_source === 'crypto' || invoice.payment_method === 'USDT' || invoice.payment_method === 'BTC';
  const paymentMethodDisplay = isCrypto 
    ? `Cryptocurrency (${invoice.payment_method || 'USDT'})` 
    : 'Credit Card (Stripe)';

  const handleDownloadPDF = () => {
    // Create a simple PDF download (in real implementation, generate proper PDF)
    const invoiceContent = `
SNAP CHART AI - INVOICE

Invoice Number: ${invoiceNumber}
Date: ${invoiceDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}

Bill To:
${userName || 'Customer'}
${userEmail || ''}

Description: ${plan?.name || invoice.plan_id} Subscription
Period: ${periodStart.toLocaleDateString()} - ${periodEnd.toLocaleDateString()}
Amount: $${invoice.amount_usd.toFixed(2)} ${invoice.currency || 'USD'}

Status: PAID
Payment Method: ${paymentMethodDisplay}

---
SnapChart AI
support@snap-chart.ai

Thank you for your business!
    `;
    
    const blob = new Blob([invoiceContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${invoiceNumber}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <motion.div
      layout
      className="bg-slate-900/80 border border-slate-700/50 rounded-xl overflow-hidden hover:border-slate-600/50 transition-all"
    >
      {/* Invoice Header - Always Visible */}
      <div 
        className="flex items-center justify-between p-4 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-4">
          {/* Status Icon */}
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
            invoice.status === 'paid' 
              ? 'bg-green-500/10 border border-green-500/30' 
              : 'bg-yellow-500/10 border border-yellow-500/30'
          }`}>
            {invoice.status === 'paid' ? (
              <FiCheck className="w-5 h-5 text-green-400" />
            ) : (
              <FiFileText className="w-5 h-5 text-yellow-400" />
            )}
          </div>
          
          {/* Invoice Info */}
          <div>
            <div className="flex items-center gap-2">
              <span className="font-mono text-sm text-slate-400">{invoiceNumber}</span>
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                invoice.status === 'paid' 
                  ? 'bg-green-500/20 text-green-400' 
                  : 'bg-yellow-500/20 text-yellow-400'
              }`}>
                {invoice.status === 'paid' ? 'Paid' : 'Pending'}
              </span>
            </div>
            <p className="text-white font-medium mt-0.5">
              {plan?.name || invoice.plan_id}
            </p>
            <p className="text-xs text-slate-500">
              {invoiceDate.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
            </p>
          </div>
        </div>
        
        {/* Amount and Expand */}
        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="text-lg font-bold text-white">${invoice.amount_usd.toFixed(2)}</p>
            <p className="text-xs text-slate-500">USD</p>
          </div>
          <motion.div
            animate={{ rotate: expanded ? 180 : 0 }}
            transition={{ duration: 0.2 }}
          >
            <FiChevronDown className="w-5 h-5 text-slate-400" />
          </motion.div>
        </div>
      </div>

      {/* Expanded Details */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 border-t border-slate-700/50 pt-4">
              {/* Invoice Details Grid */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Invoice Number</p>
                  <p className="text-sm text-white font-mono">{invoiceNumber}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Invoice Date</p>
                  <p className="text-sm text-white">
                    {invoiceDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Billing Period</p>
                  <p className="text-sm text-white">
                    {periodStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} — {periodEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Payment Method</p>
                  <p className="text-sm text-white">{paymentMethodDisplay}</p>
                </div>
              </div>

              {/* Line Items */}
              <div className="bg-slate-800/50 rounded-lg p-4 mb-4">
                <div className="flex justify-between items-center pb-2 border-b border-slate-700/50 mb-2">
                  <span className="text-xs text-slate-500 uppercase tracking-wider">Description</span>
                  <span className="text-xs text-slate-500 uppercase tracking-wider">Amount</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <div>
                    <p className="text-white font-medium">{plan?.name || invoice.plan_id} Plan</p>
                    <p className="text-xs text-slate-500">
                      {invoice.billing_type === 'annual' ? 'Annual subscription' : 
                       invoice.billing_type === 'lifetime' ? 'Lifetime access' : 
                       'Monthly subscription'}
                    </p>
                  </div>
                  <p className="text-white font-medium">${invoice.amount_usd.toFixed(2)}</p>
                </div>
                <div className="flex justify-between items-center pt-2 border-t border-slate-700/50 mt-2">
                  <span className="text-white font-bold">Total</span>
                  <span className="text-lg font-bold text-white">${invoice.amount_usd.toFixed(2)} USD</span>
                </div>
              </div>

              {/* Payment Status - All invoices from billing_events are paid */}
              <div className="flex items-center gap-2 p-3 bg-green-500/10 border border-green-500/20 rounded-lg mb-4">
                <FiCheck className="w-4 h-4 text-green-400" />
                <span className="text-sm text-green-400">
                  Paid on {invoiceDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                </span>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDownloadPDF();
                  }}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 text-white text-sm font-medium hover:from-purple-600/30 hover:to-cyan-600/30 transition-all"
                >
                  <FiDownload className="w-4 h-4" />
                  Download Receipt
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default InvoiceCard;
